/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or
 * distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly
 * prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from 'react'
import clsx from 'clsx'
import { ColumnFiltersState, SortingState, VisibilityState, ColumnOrderState } from '@tanstack/react-table'

import UiButton, {
    UiButtonSize,
    UiButtonStyle,
} from 'components/Ui/UiButton'
import { useAppDispatch } from 'helpers/store'
import { TableViewId } from 'types/enums/specific/TableViewId'
import { TableUiState } from 'utils/hooks/useTable'
import { clearActiveTableView } from 'utils/table/activeTableView'
import { ArrowPathIcon } from '@heroicons/react/24/outline'

interface UiTableHeaderResetButtonTableApi {
    setPagination: (pagination: { pageIndex: number; pageSize: number }) => void
    setColumnFilters: (filters: ColumnFiltersState) => void
    setGlobalFilter: (value: string) => void
    setSorting: (sorting: SortingState) => void
    setColumnVisibility?: (visibility: VisibilityState) => void
    setColumnOrder?: (order: ColumnOrderState) => void
}

interface UiTableHeaderResetButtonProps {
    tableInit: UiTableHeaderResetButtonTableApi
    defaultUiState: TableUiState
    title: string
    tableId?: TableViewId
    onAfterReset?: () => void
    icon?: React.ReactNode
}

const UiTableHeaderResetButton: React.FC<UiTableHeaderResetButtonProps> = ({
    tableInit,
    defaultUiState,
    title,
    tableId,
    onAfterReset,
    icon,
}) => {
    const dispatch = useAppDispatch()

    const handleClick = () => {
        if (!defaultUiState) {
            return
        }

        tableInit.setPagination({
            pageIndex: defaultUiState.pageIndex,
            pageSize: defaultUiState.pageSize,
        })
        tableInit.setColumnFilters(defaultUiState.columnFilters)
        tableInit.setGlobalFilter(defaultUiState.globalFilter)
        tableInit.setSorting(defaultUiState.sorting)
        if (tableInit.setColumnVisibility) {
            tableInit.setColumnVisibility(defaultUiState.columnVisibility ?? {})
        }
        if (tableInit.setColumnOrder) {
            tableInit.setColumnOrder(defaultUiState.columnOrder ?? [])
        }

        if (tableId) {
            clearActiveTableView(dispatch, tableId)
        }

        onAfterReset?.()
    }

    return (
        <UiButton
            className={clsx('UiTableHeaderResetButton')}
            size={UiButtonSize.Normal}
            style={UiButtonStyle.Secondary}
            title={title}
            onClick={handleClick}
            icon={icon ?? <ArrowPathIcon className="w-5 h-5" />}
        />
    )
}

export default UiTableHeaderResetButton
